import {
  auditActorLabel,
  auditBoundary,
  buildAuditSummary,
  changedItemLabel,
  describeFieldChanges,
  identifier,
  formatAuditTime,
  isVisibleBusinessAudit,
  redactForDisplay,
  toLosAngelesBoundary,
} from './audit-utils.mjs?v=20260806.100';
import {
  auditFieldLabel,
  isInternalAuditField,
  narrateAuditEntry,
  narrateFieldChange,
  technicalNoteText,
} from './audit-change-narrative.mjs?v=20260806.100';
import { getLocale, setLocale, subscribeLocale, t, tHtml } from './i18n.mjs?v=20260806.100';

const PAGE_SIZE = 100;
const MAX_SEARCH_RANGE_MS = 90 * 24 * 60 * 60 * 1000;

function escapeHtml(value) {
  return String(value ?? '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');
}

function localeKey(locale) {
  return String(locale || '').toLowerCase().startsWith('zh') ? 'zh-CN' : 'en';
}

function strings(locale) {
  const value = (key, variables) => t(`audit.${key}`, variables, localeKey(locale));
  return {
    title: value('title'), subtitle: value('subtitle'), pageSize: value('pageSize'),
    startDate: value('startDate'), startTime: value('startTime'),
    endDate: value('endDate'), endTime: value('endTime'), search: value('search'),
    clear: value('clear'), time: value('time'), actor: value('actor'), item: value('item'),
    summary: value('summary'), details: value('details'),
    older: value('older'), newer: value('newer'), pagination: value('pagination'),
    loading: value('loading'), empty: value('empty'), collection: value('collection'),
    documentId: value('documentId'), operation: value('operation'), role: value('role'),
    eventTime: value('eventTime'), changedFields: value('changedFields'),
    previous: value('previous'), next: value('next'), reason: value('reason'),
    logId: value('logId'),
    technical: value('technical'), loadError: value('loadError'),
    invalidRange: value('invalidRange'), missingRange: value('missingRange'),
    longRange: value('longRange'),
    showDetails: (item) => value('showDetails', { item }),
    hideDetails: (item) => value('hideDetails', { item }),
    showDetailsHtml: (item) => tHtml('audit.showDetails', { item }, localeKey(locale)),
    hideDetailsHtml: (item) => tHtml('audit.hideDetails', { item }, localeKey(locale)),
    newEvents: (count) => value(count === 1 ? 'newEvent' : 'newEvents', { count }),
  };
}

function technicalJson(log) {
  return JSON.stringify(redactForDisplay({
    before: log.before_data ?? log.old_value ?? null,
    after: log.after_data ?? log.new_value ?? null,
  }), null, 2);
}

/**
 * What the person did, in sentences, above the field-by-field table.
 *
 * Alan screenshots these as evidence and shows them to people who do not read
 * databases (2026-08-08). The table alone said things like `search_keywords`
 * changed from one array to the same array reordered - true, and no use to
 * anybody. The sentences lead; the table stays underneath for the detail; and
 * the system's own bookkeeping is counted rather than listed, because 319 of 400
 * production entries carry an updated_at change and none of them is news.
 */
/**
 * The one-line summary in the list. "Container TCNU1647316 was updated" is true
 * of almost every row and tells nobody anything (Alan, 2026-08-08), so the line
 * carries the change itself: the first business change, and how many more there
 * were. buildAuditSummary still answers for the entries it words specially -
 * cancellations, pickups, empty returns.
 */
export function auditSummaryText(log, locale = 'en') {
  const zh = String(locale).toLowerCase().startsWith('zh');
  const { lines } = narrateAuditEntry({
    changes: describeFieldChanges(log, locale),
    operation: log.operation_type || log.action || 'updated',
    collection: log.target_collection || String(log.target_path || '').split('/')[0] || '',
    identifier: identifier(log),
  }, locale);
  const operation = String(log.operation_type || log.action || 'updated');
  if (operation === 'created' || operation === 'deleted') return lines[0];
  if (!lines.length) return buildAuditSummary(log, locale);
  const head = `${identifier(log)}：${lines[0]}`;
  if (lines.length === 1) return head;
  return zh ? `${head}，另有 ${lines.length - 1} 项改动`
    : `${head} (+${lines.length - 1} more)`;
}

function narrativeBlock(log, locale) {
  const changes = describeFieldChanges(log, locale);
  const { lines, internalCount } = narrateAuditEntry({
    changes,
    operation: log.operation_type || log.action || 'updated',
    collection: log.target_collection || String(log.target_path || '').split('/')[0] || '',
    identifier: identifier(log),
  }, locale);
  const note = technicalNoteText(internalCount, locale);
  return `
    <ul class="audit-narrative">
      ${lines.map((line) => `<li>${escapeHtml(line)}</li>`).join('')}
    </ul>
    ${note ? `<p class="audit-narrative-note muted">${escapeHtml(note)}</p>` : ''}
  `;
}

function changeRows(log, text, locale) {
  // Business fields only. Nothing is lost: the raw before/after for every field,
  // bookkeeping included, is still in Technical details below.
  const changes = describeFieldChanges(log, locale)
    .filter((change) => !isInternalAuditField(change.fieldPath));
  if (!changes.length) {
    return `<div class="audit-no-field-changes muted">${escapeHtml(text.empty)}</div>`;
  }
  return `
    <div class="audit-field-change-list" role="table" aria-label="${escapeHtml(text.changedFields)}">
      <div class="audit-change-head" role="row">
        <span role="columnheader">${escapeHtml(text.changedFields)}</span>
        <span role="columnheader">${escapeHtml(text.previous)}</span>
        <span role="columnheader">${escapeHtml(text.next)}</span>
      </div>
      ${changes.map((change) => `
        <div class="audit-change-row" role="row">
          <span role="cell"><b>${escapeHtml(auditFieldLabel(change.fieldPath, locale))}</b><small>${escapeHtml(narrateFieldChange(change, locale))}</small></span>
          <span class="audit-change-value" role="cell"><small class="audit-mobile-change-label">${escapeHtml(text.previous)}</small>${escapeHtml(change.before)}</span>
          <span class="audit-change-value" role="cell"><small class="audit-mobile-change-label">${escapeHtml(text.next)}</small>${escapeHtml(change.after)}</span>
        </div>
      `).join('')}
    </div>
  `;
}

function detailPanel(log, text, locale) {
  const targetCollection = log.target_collection || String(log.target_path || '').split('/')[0] || '';
  const targetDocId = log.target_doc_id || String(log.target_path || '').split('/')[1] || log.entity_ref?.id || '';
  return `
    <div class="audit-detail-panel" id="audit-detail-${escapeHtml(log.id)}">
      ${narrativeBlock(log, locale)}
      <dl class="audit-detail-meta">
        <div><dt>${escapeHtml(text.collection)}</dt><dd>${escapeHtml(targetCollection)}</dd></div>
        <div><dt>${escapeHtml(text.documentId)}</dt><dd>${escapeHtml(targetDocId)}</dd></div>
        <div><dt>${escapeHtml(text.operation)}</dt><dd>${escapeHtml(log.operation_type || log.action || 'updated')}</dd></div>
        <div><dt>${escapeHtml(text.actor)}</dt><dd>${escapeHtml(auditActorLabel(log, locale))}</dd></div>
        <div><dt>${escapeHtml(text.role)}</dt><dd>${escapeHtml(log.actor_role || '')}</dd></div>
        <div><dt>${escapeHtml(text.eventTime)}</dt><dd>${escapeHtml(formatAuditTime(log.performed_at || log.created_at, locale))}</dd></div>
        <div><dt>${escapeHtml(text.reason)}</dt><dd>${escapeHtml(log.reason || '-')}</dd></div>
        <div><dt>${escapeHtml(text.logId)}</dt><dd>${escapeHtml(log.id)}</dd></div>
      </dl>
      ${changeRows(log, text, locale)}
      <details class="audit-technical-details">
        <summary>${escapeHtml(text.technical)}</summary>
        <pre>${escapeHtml(technicalJson(log))}</pre>
      </details>
    </div>
  `;
}

function tableMarkup(logs, text, locale, openIds) {
  if (!logs.length) {
    return `<div class="audit-empty-state" role="status">${escapeHtml(text.empty)}</div>`;
  }
  return `
    <div class="audit-table-wrap">
      <table class="audit-business-table">
        <thead><tr>
          <th>${escapeHtml(text.time)}</th>
          <th>${escapeHtml(text.actor)}</th>
          <th>${escapeHtml(text.item)}</th>
          <th>${escapeHtml(text.summary)}</th>
          <th>${escapeHtml(text.details)}</th>
        </tr></thead>
        <tbody>
          ${logs.map((log) => {
            const item = changedItemLabel(log, locale);
            const isOpen = openIds.has(log.id);
            return `
              <tr data-audit-row data-audit-id="${escapeHtml(log.id)}">
                <td data-label="${escapeHtml(text.time)}">${escapeHtml(formatAuditTime(log.performed_at || log.created_at, locale))}</td>
                <td data-label="${escapeHtml(text.actor)}">${escapeHtml(auditActorLabel(log, locale))}</td>
                <td data-label="${escapeHtml(text.item)}">${escapeHtml(item)}</td>
                <td data-label="${escapeHtml(text.summary)}">${escapeHtml(auditSummaryText(log, locale))}</td>
                <td data-label="${escapeHtml(text.details)}">
                  <button class="icon-button audit-expand-button" type="button"
                    aria-expanded="${isOpen}"
                    aria-controls="audit-detail-${escapeHtml(log.id)}"
                    aria-label="${(isOpen ? text.hideDetailsHtml : text.showDetailsHtml)(item)}"
                    data-audit-expand="${escapeHtml(log.id)}">
                    <span class="audit-chevron" aria-hidden="true">&rsaquo;</span>
                  </button>
                </td>
              </tr>
              <tr class="audit-detail-row" data-audit-detail-row="${escapeHtml(log.id)}" ${isOpen ? '' : 'hidden'}>
                <td colspan="5">${detailPanel(log, text, locale)}</td>
              </tr>
            `;
          }).join('')}
        </tbody>
      </table>
    </div>
  `;
}

export function mountAdminAuditLog({
  container,
  source,
  locale = getLocale(),
  setLocalePreference = setLocale,
}) {
  if (!container) throw new Error('Admin audit container is required.');
  if (!source?.loadPage) throw new Error('Admin audit data source is required.');

  let destroyed = false;
  let unsubscribeNewer = null;
  let currentLogs = [];
  let hasOlder = false;
  let pageHistory = [];
  let range = null;
  let openIds = new Set();
  let pendingNewLogs = [];
  let activeLocale = localeKey(locale);
  let text = strings(activeLocale);
  let statusKey = null;
  let statusSuffix = '';

  container.innerHTML = `
    <section class="admin-audit-timeline" aria-labelledby="adminAuditHeading">
      <div class="audit-heading-row">
        <div>
          <h2 id="adminAuditHeading" data-audit-copy="title">${escapeHtml(text.title)}</h2>
          <p data-audit-copy="subtitle">${escapeHtml(text.subtitle)}</p>
        </div>
        <label class="audit-locale-picker">
          <span data-language-label>${escapeHtml(t('language.label', {}, activeLocale))}</span>
          <select data-locale-select aria-label="${escapeHtml(t('language.label', {}, activeLocale))}">
            <option value="en" ${activeLocale === 'en' ? 'selected' : ''}>EN</option>
            <option value="zh-CN" ${activeLocale === 'zh-CN' ? 'selected' : ''}>中文</option>
          </select>
        </label>
        <span class="audit-page-size" data-audit-copy="pageSize">${escapeHtml(text.pageSize)}</span>
      </div>
      <form class="audit-range-form" novalidate>
        <label><span data-audit-copy="startDate">${escapeHtml(text.startDate)}</span><input name="start_date" type="date" aria-label="${escapeHtml(text.startDate)}"></label>
        <label><span data-audit-copy="startTime">${escapeHtml(text.startTime)}</span><input name="start_time" type="time" step="1" aria-label="${escapeHtml(text.startTime)}"></label>
        <label><span data-audit-copy="endDate">${escapeHtml(text.endDate)}</span><input name="end_date" type="date" aria-label="${escapeHtml(text.endDate)}"></label>
        <label><span data-audit-copy="endTime">${escapeHtml(text.endTime)}</span><input name="end_time" type="time" step="1" aria-label="${escapeHtml(text.endTime)}"></label>
        <div class="audit-search-actions">
          <button class="primary" type="submit" data-audit-copy="search">${escapeHtml(text.search)}</button>
          <button class="secondary" type="button" data-audit-clear data-audit-copy="clear">${escapeHtml(text.clear)}</button>
        </div>
      </form>
      <div class="audit-feedback-row">
        <button class="audit-new-events" type="button" hidden></button>
        <div class="audit-status" role="status" aria-live="polite"></div>
      </div>
      <div class="audit-results"></div>
      <nav class="audit-pagination" aria-label="${escapeHtml(text.pagination)}">
        <button class="secondary" type="button" data-audit-newer data-audit-copy="newer" disabled>${escapeHtml(text.newer)}</button>
        <button class="secondary" type="button" data-audit-older data-audit-copy="older" disabled>${escapeHtml(text.older)}</button>
      </nav>
    </section>
  `;

  const form = container.querySelector('.audit-range-form');
  const results = container.querySelector('.audit-results');
  const status = container.querySelector('.audit-status');
  const olderButton = container.querySelector('[data-audit-older]');
  const newerButton = container.querySelector('[data-audit-newer]');
  const clearButton = container.querySelector('[data-audit-clear]');
  const newEventsButton = container.querySelector('.audit-new-events');
  const localeSelect = container.querySelector('[data-locale-select]');

  function setStatus(key = null, suffix = '') {
    statusKey = key;
    statusSuffix = suffix;
    status.textContent = key ? `${text[key]} ${suffix}`.trim() : '';
  }

  function visibleLogs(logs) {
    return logs.filter(isVisibleBusinessAudit);
  }

  function updatePagination() {
    olderButton.disabled = !hasOlder || currentLogs.length === 0;
    newerButton.disabled = pageHistory.length === 0;
  }

  function bindRows() {
    container.querySelectorAll('[data-audit-expand]').forEach((button) => {
      button.addEventListener('click', () => {
        const id = button.dataset.auditExpand;
        const row = container.querySelector(`[data-audit-detail-row="${CSS.escape(id)}"]`);
        const item = changedItemLabel(currentLogs.find((log) => log.id === id), activeLocale);
        const willOpen = !openIds.has(id);
        if (willOpen) openIds.add(id); else openIds.delete(id);
        button.setAttribute('aria-expanded', String(willOpen));
        button.setAttribute('aria-label', (willOpen ? text.hideDetails : text.showDetails)(item));
        row.hidden = !willOpen;
      });
    });
  }

  function renderLogs() {
    results.innerHTML = tableMarkup(currentLogs, text, activeLocale, openIds);
    updatePagination();
    bindRows();
  }

  function applyLocale(nextLocale) {
    activeLocale = localeKey(nextLocale);
    text = strings(activeLocale);
    container.querySelectorAll('[data-audit-copy]').forEach((element) => {
      element.textContent = text[element.dataset.auditCopy];
    });
    container.querySelectorAll('[data-language-label]').forEach((element) => {
      element.textContent = t('language.label', {}, activeLocale);
    });
    localeSelect.value = activeLocale;
    localeSelect.setAttribute('aria-label', t('language.label', {}, activeLocale));
    for (const [name, key] of [
      ['start_date', 'startDate'], ['start_time', 'startTime'],
      ['end_date', 'endDate'], ['end_time', 'endTime'],
    ]) {
      form.elements[name].setAttribute('aria-label', text[key]);
    }
    container.querySelector('.audit-pagination').setAttribute('aria-label', text.pagination);
    setStatus(statusKey, statusSuffix);
    if (pendingNewLogs.length) {
      newEventsButton.textContent = text.newEvents(pendingNewLogs.length);
      newEventsButton.setAttribute('aria-label', text.newEvents(pendingNewLogs.length));
    }
    if (statusKey === 'loadError') {
      results.innerHTML = `<div class="audit-error-state" role="alert">${escapeHtml(status.textContent)}</div>`;
    } else {
      renderLogs();
    }
  }

  localeSelect.addEventListener('change', () => {
    const nextLocale = setLocalePreference(localeSelect.value);
    if (activeLocale !== nextLocale) applyLocale(nextLocale);
  });
  const unsubscribeLocale = subscribeLocale(applyLocale);

  function setupNewerSubscription() {
    unsubscribeNewer?.();
    unsubscribeNewer = null;
    if (!source.subscribeNewer || !currentLogs.length) return;
    const boundary = auditBoundary(currentLogs[0]);
    unsubscribeNewer = source.subscribeNewer(boundary, (logs) => {
      const visible = visibleLogs(logs).filter((log) => !currentLogs.some((current) => current.id === log.id));
      if (!visible.length) return;
      pendingNewLogs = visible;
      newEventsButton.hidden = false;
      newEventsButton.textContent = text.newEvents(visible.length);
      newEventsButton.setAttribute('aria-label', text.newEvents(visible.length));
    }, range);
  }

  async function loadPage({ cursor = null, keepHistory = false } = {}) {
    setStatus('loading');
    results.setAttribute('aria-busy', 'true');
    try {
      const response = await source.loadPage({
        limit: PAGE_SIZE,
        cursor,
        rangeStartMillis: range?.startMillis ?? null,
        rangeEndMillis: range?.endMillis ?? null,
      });
      if (destroyed) return;
      if (!keepHistory) pageHistory = [];
      currentLogs = visibleLogs(response.logs || []);
      hasOlder = Boolean(response.hasOlder ?? (response.logs || []).length === PAGE_SIZE);
      setStatus();
      pendingNewLogs = [];
      newEventsButton.hidden = true;
      renderLogs();
      setupNewerSubscription();
    } catch (error) {
      if (destroyed) return;
      setStatus('loadError', error?.message || '');
      results.innerHTML = `<div class="audit-error-state" role="alert">${escapeHtml(status.textContent)}</div>`;
      hasOlder = false;
      updatePagination();
    } finally {
      results.removeAttribute('aria-busy');
    }
  }

  olderButton.addEventListener('click', async () => {
    if (!currentLogs.length) return;
    pageHistory.push({ logs: currentLogs, hasOlder });
    await loadPage({ cursor: auditBoundary(currentLogs.at(-1)), keepHistory: true });
  });

  newerButton.addEventListener('click', () => {
    const previous = pageHistory.pop();
    if (!previous) return;
    currentLogs = previous.logs;
    hasOlder = previous.hasOlder;
    renderLogs();
    setupNewerSubscription();
  });

  newEventsButton.addEventListener('click', () => {
    if (!pendingNewLogs.length) return;
    loadPage({ keepHistory: false });
  });

  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    const data = new FormData(form);
    const startDate = String(data.get('start_date') || '');
    const startTime = String(data.get('start_time') || '');
    const endDate = String(data.get('end_date') || '');
    const endTime = String(data.get('end_time') || '');
    if (!startDate || !startTime || !endDate || !endTime) {
      setStatus('missingRange');
      return;
    }
    try {
      const startMillis = toLosAngelesBoundary(startDate, startTime);
      const endMillis = toLosAngelesBoundary(endDate, endTime);
      if (endMillis <= startMillis) {
        setStatus('invalidRange');
        return;
      }
      if (endMillis - startMillis > MAX_SEARCH_RANGE_MS && !window.confirm(text.longRange)) return;
      range = { startMillis, endMillis };
      openIds = new Set();
      await loadPage({ keepHistory: false });
    } catch (error) {
      setStatus('invalidRange');
    }
  });

  clearButton.addEventListener('click', () => {
    form.reset();
    range = null;
    openIds = new Set();
    loadPage({ keepHistory: false });
  });

  loadPage({ keepHistory: false });

  return {
    destroy() {
      destroyed = true;
      unsubscribeLocale();
      unsubscribeNewer?.();
      unsubscribeNewer = null;
    },
    refresh() {
      return loadPage({ keepHistory: false });
    },
  };
}
